import { Button, Collapsible } from "@simplicity/ui";
import { CircleAlert, X } from "lucide-react";
import { useId, useState } from "react";
import type { ScheduleEntry, TableConfig } from "../tables/types";
import { AddTablesPicker } from "./AddTablesPicker";
import { auditSchedules } from "./scheduleAudit";
import type { ScheduleRow, ScheduleTable } from "./scheduleRows";
import { ScheduleTableOptions } from "./ScheduleTableOptions";

/**
 * A schedule's tables, on the schedule's own row (SIM-3402). The config is schedule-major
 * and the old editor was table-major, so "what runs daily?" meant expanding tables one at a
 * time and reading a checkbox four levels down. Here the schedule lists its own roster, and
 * a table name opens its options for this schedule, inline under its row.
 */

export interface ScheduleRosterProps {
  row: ScheduleRow;
  /** Every table on the connector — `vars.tables`. */
  tables: TableConfig[];
  /** Omit to render read-only, matching the panel's own editable rule. */
  onAddTables?: (names: string[]) => void;
  onRemoveTable?: (name: string) => void;
  /** Omit and the options panel still opens, read-only — the roster summary is lossy. */
  onTableEntriesChange?: (tableName: string, entries: ScheduleEntry[]) => void;
}

/** This schedule's entries that no definition matches (SIM-3044) — the audit, over one row. */
function undefinedOnRow(row: ScheduleRow, tables: TableConfig[]): Set<string> {
  const entries = row.tables.flatMap((t) => t.entries);
  return new Set(auditSchedules(tables, { [row.name]: entries }).undefinedScheduled[row.name]);
}

function NotDefinedNote({ id, children }: { id?: string; children: string }) {
  return (
    <p id={id} className="flex items-center gap-1 text-xs font-medium text-destructive">
      <CircleAlert className="h-3 w-3 shrink-0" aria-hidden="true" />
      {children}
    </p>
  );
}

function RosterItem({
  table,
  definition,
  notDefined,
  scheduleName,
  onRemove,
  onEntriesChange,
}: {
  table: ScheduleTable;
  definition: TableConfig | undefined;
  notDefined: boolean;
  scheduleName: string;
  onRemove: (() => void) | undefined;
  onEntriesChange: ((entries: ScheduleEntry[]) => void) | undefined;
}) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const noteId = useId();

  // Name and summary are one control, on one line: the summary is what you read to decide
  // whether to open the options, so making it the thing you click saves a hop (SIM-3515).
  // The summary is CONFIGURED, never observed — nothing here claims a run used it (SIM-2891).
  return (
    <li className="border-t border-border/40 py-1 first:border-t-0">
      <div className="flex items-baseline justify-between gap-2">
        <Button
          type="button"
          variant="link"
          className="h-auto min-w-0 justify-start gap-2 p-0 text-left"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          aria-label={`Options for ${table.name} on ${scheduleName}`}
          aria-describedby={notDefined ? noteId : undefined}
        >
          <span className="font-mono text-sm">{table.name}</span>
          {table.summary && (
            <span className="truncate text-xs font-normal text-muted-foreground">
              {table.summary}
            </span>
          )}
        </Button>
        {onRemove && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="shrink-0"
            onClick={onRemove}
            aria-label={`Remove ${table.name} from ${scheduleName}`}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        )}
      </div>
      {notDefined && (
        <NotDefinedNote id={noteId}>
          Not defined under Tables, so this fails every run.
        </NotDefinedNote>
      )}
      {open && (
        <ScheduleTableOptions
          id={panelId}
          scheduleName={scheduleName}
          tableName={table.name}
          table={definition}
          entries={table.entries}
          onEntriesChange={onEntriesChange}
        />
      )}
    </li>
  );
}

function RosterBody({
  row,
  tables,
  notDefined,
  onAddTables,
  onRemoveTable,
  onTableEntriesChange,
}: ScheduleRosterProps & { notDefined: Set<string> }) {
  // Case-insensitive, as `applySchedule` merges an entry onto its definition — an exact match
  // would call a case-drifted entry an orphan that the run resolves without complaint.
  const byName = new Map(tables.map((t) => [t.name.toLowerCase(), t]));

  return (
    <>
      {row.tables.length > 0 && (
        <ul className="mb-1">
          {row.tables.map((table) => (
            <RosterItem
              key={table.name}
              table={table}
              definition={byName.get(table.name.toLowerCase())}
              notDefined={notDefined.has(table.name)}
              scheduleName={row.name}
              onRemove={onRemoveTable ? () => onRemoveTable(table.name) : undefined}
              onEntriesChange={
                onTableEntriesChange
                  ? (entries) => onTableEntriesChange(table.name, entries)
                  : undefined
              }
            />
          ))}
        </ul>
      )}
      {onAddTables && (
        <AddTablesPicker
          scheduleName={row.name}
          tableNames={tables.map((t) => t.name)}
          onSchedule={row.tables.map((t) => t.name)}
          onAdd={onAddTables}
        />
      )}
    </>
  );
}

export function ScheduleRoster(props: ScheduleRosterProps) {
  const { row, tables, onAddTables } = props;
  const notDefined = undefinedOnRow(row, tables);

  // Nothing to expand on an empty schedule, and hiding the only way to fill it behind a
  // disclosure is the state least worth hiding — this is how a new schedule gets tables.
  if (row.tables.length === 0) {
    return onAddTables ? (
      <div className="min-w-[22rem]">
        <RosterBody {...props} notDefined={notDefined} />
      </div>
    ) : (
      <span className="text-muted-foreground">none</span>
    );
  }

  const label = `${row.tables.length} table${row.tables.length === 1 ? "" : "s"}`;
  // Every row's disclosure would otherwise be called "2 tables" — the column says which
  // schedule, the control does not, and two same-named controls fail SC 2.4.6. The name is
  // built in one node because an adjacent node's trailing space is collapsed out of it.
  const trigger = (
    <>
      <span className="sr-only">{`${row.name} — ${label}`}</span>
      <span aria-hidden="true">{label}</span>
    </>
  );
  // Outside the disclosure: a table that fails every run must show while the roster is collapsed.
  const failing =
    notDefined.size === 1
      ? "1 scheduled table isn't defined, so it fails every run."
      : `${notDefined.size} scheduled tables aren't defined, so they fail every run.`;
  return (
    <div className="min-w-[22rem]">
      {notDefined.size > 0 && <NotDefinedNote>{failing}</NotDefinedNote>}
      <Collapsible trigger={trigger} triggerClassName="font-medium">
        <RosterBody {...props} notDefined={notDefined} />
      </Collapsible>
    </div>
  );
}
